import React from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, Alert } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useColors, ThemeColors } from '../../hooks/useColors';
import { spacing, typography } from '../../theme';
import { Button } from './Button';

interface StripeVerificationModalProps {
  visible: boolean;
  onClose: () => void;
  stripeStatus?: string | null;
  title?: string;
  description?: string;
}

export function StripeVerificationModal({
  visible,
  onClose,
  stripeStatus,
  title,
  description,
}: StripeVerificationModalProps) {
  const colors = useColors();
  const navigation = useNavigation();
  const styles = createStyles(colors);

  const getStatusInfo = () => {
    switch (stripeStatus) {
      case 'pending':
        return {
          icon: 'clock',
          iconColor: colors.warning,
          title: 'Stripe Setup Pending',
          message: 'Your Stripe Express account setup is in progress. Please complete your onboarding to start selling.',
          actionText: 'Complete Setup',
        };
      case 'active':
        return {
          icon: 'check-circle',
          iconColor: colors.success,
          title: 'Stripe Connected',
          message: 'Your Stripe account is active and ready for payments.',
          actionText: 'Continue',
        };
      default:
        return {
          icon: 'credit-card',
          iconColor: colors.primary,
          title: 'Connect Stripe Account',
          message: 'You need to connect your Stripe Express account to enable payments and start selling products.',
          actionText: 'Set Up Payments',
        };
    }
  };

  const statusInfo = getStatusInfo();
  const modalTitle = title || statusInfo.title;
  const modalDescription = description || statusInfo.message;

  const handleAction = () => {
    onClose();
    // Navigate to vendor settings where Stripe setup is handled
    navigation.navigate('VendorSettings' as never);
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modal}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.iconContainer}>
              <Feather
                name={statusInfo.icon as any}
                size={32}
                color={statusInfo.iconColor}
              />
            </View>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
            >
              <Feather name="x" size={24} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Content */}
          <View style={styles.content}>
            <Text style={styles.title}>{modalTitle}</Text>
            <Text style={styles.description}>{modalDescription}</Text>

            {/* Progress Indicator */}
            {stripeStatus !== 'active' && (
              <View style={styles.progressContainer}>
                <Text style={styles.progressTitle}>Setup Progress</Text>
                <View style={styles.progressBar}>
                  <View
                    style={[
                      styles.progressFill,
                      {
                        width: stripeStatus === 'pending' ? '50%' : '0%',
                      },
                    ]}
                  />
                </View>
                <View style={styles.progressSteps}>
                  <View style={styles.progressStep}>
                    <View
                      style={[
                        styles.stepIndicator,
                        stripeStatus ? styles.stepCompleted : styles.stepPending,
                      ]}
                    />
                    <Text style={styles.stepText}>Account Created</Text>
                  </View>
                  <View style={styles.progressStep}>
                    <View
                      style={[
                        styles.stepIndicator,
                        stripeStatus === 'active' ? styles.stepCompleted : styles.stepPending,
                      ]}
                    />
                    <Text style={styles.stepText}>Setup Complete</Text>
                  </View>
                </View>
              </View>
            )}
          </View>

          {/* Actions */}
          <View style={styles.actions}>
            <Button
              onPress={handleCancel}
              variant="outline"
              style={styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              onPress={handleAction}
              variant="primary"
              style={styles.actionButton}
            >
              {statusInfo.actionText}
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.lg,
    },
    modal: {
      backgroundColor: colors.surface,
      borderRadius: spacing.lg,
      maxWidth: 400,
      width: '100%',
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: spacing.lg,
      paddingBottom: spacing.sm,
    },
    iconContainer: {
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: colors.backgroundSecondary,
      justifyContent: 'center',
      alignItems: 'center',
    },
    closeButton: {
      padding: spacing.xs,
    },
    content: {
      padding: spacing.lg,
      paddingTop: 0,
    },
    title: {
      ...typography.title,
      color: colors.text,
      marginBottom: spacing.sm,
      textAlign: 'center',
    },
    description: {
      ...typography.body,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 22,
      marginBottom: spacing.lg,
    },
    progressContainer: {
      marginTop: spacing.md,
    },
    progressTitle: {
      ...typography.subtitle,
      color: colors.text,
      fontSize: 16,
      marginBottom: spacing.sm,
    },
    progressBar: {
      height: 6,
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 3,
      marginBottom: spacing.md,
    },
    progressFill: {
      height: '100%',
      backgroundColor: colors.primary,
      borderRadius: 3,
    },
    progressSteps: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    progressStep: {
      alignItems: 'center',
    },
    stepIndicator: {
      width: 12,
      height: 12,
      borderRadius: 6,
      marginBottom: spacing.xs,
    },
    stepCompleted: {
      backgroundColor: colors.success,
    },
    stepPending: {
      backgroundColor: colors.backgroundSecondary,
      borderWidth: 2,
      borderColor: colors.border,
    },
    stepText: {
      ...typography.caption,
      color: colors.textSecondary,
      fontSize: 12,
    },
    actions: {
      flexDirection: 'row',
      padding: spacing.lg,
      paddingTop: 0,
      gap: spacing.md,
    },
    cancelButton: {
      flex: 1,
    },
    actionButton: {
      flex: 2,
    },
  });